import type { ScheduleTime } from '@src/core/buyerApiClient';
import type { TariffsPerType } from '@src/stores/product';
import { scheduleIsAvailable } from '@src/domains/tariffs';
import { hasSeatMapAvailability } from '@src/common/seatMaps';
import type { BuildHoursListInput, HourListItem, ValidationStatus } from '@src/domains/products/scheduleHours/types';

export type { BuildHoursListInput, HourListItem, ValidationStatus } from '@src/domains/products/scheduleHours/types';

export { buildLanguageOptionsForSchedulings, schedulingsForTariffMount } from './core/filterSchedulingsByLanguage';
export {
  buildAttributeSlotsForSchedulings,
  filterSchedulingsByAttribute,
  pruneSelectedAttributes,
  resolveSchedulingsForAttributes,
  schedulingsForTariffMountWithAttributes,
} from './core/filterSchedulingsByAttributes';

/**
 * Every time the selected tariffs have a scheduling for on the date, sold-out ones included, in
 * chronological order. Sold-out schedulings sit in `soldOutSchedulings`, so no open scheduling
 * contains their times and buildHoursList flags them as errors — listed, but disabled.
 */
export function getScheduleTimesIncludingSoldOut(selectedTariffsPerType: TariffsPerType[]): ScheduleTime[] {
  const timesMap: Record<string, ScheduleTime> = {};

  for (const { schedulings, soldOutSchedulings = [] } of selectedTariffsPerType) {
    for (const scheduling of [...schedulings, ...soldOutSchedulings]) {
      for (const time of scheduling.availableScheduleTimes ?? []) {
        timesMap[time.formattedTime] = time;
      }
    }
  }

  return Object.values(timesMap).sort((a, b) => a.minutes - b.minutes);
}

export function isValidationCurrent(selectedTariffsHash: string, validationTariffHash: string | undefined): boolean {
  if (!validationTariffHash) return false;
  return selectedTariffsHash === validationTariffHash;
}

export function canCheckSeatMap(
  hasSchedulingRequiringSeat: boolean,
  validationStatus: ValidationStatus,
  isCurrent: boolean,
): boolean {
  return hasSchedulingRequiringSeat && validationStatus === 'completed' && isCurrent;
}

type DecideHourAutoSelectionOptions = {
  /** When true, clears a selected hour that is not in hoursList (package single-booking flow). */
  requireSelectionInList?: boolean;
};

export function decideHourAutoSelection(
  hoursList: HourListItem[],
  selectedHour: ScheduleTime | undefined,
  options?: DecideHourAutoSelectionOptions,
): { clearSelection: boolean; autoSelectValue: ScheduleTime | undefined } {
  const hoursWithErrors = hoursList?.filter((value) => value.error) || [];
  const selectedHourHasError = hoursWithErrors.some(
    (value) => value.hour.formattedTime === selectedHour?.formattedTime,
  );
  const selectedHourNotInList =
    options?.requireSelectionInList &&
    selectedHour &&
    !hoursList.some((item) => item.hour.minutes === selectedHour.minutes);
  const clearWhenListIsEmpty = options?.requireSelectionInList && hoursList.length === 0 && Boolean(selectedHour);

  const autoSelectValue = hoursList.length === 1 && !hoursList[0]!.error ? hoursList[0]!.hour : undefined;

  return {
    clearSelection: Boolean(selectedHourHasError || selectedHourNotInList || clearWhenListIsEmpty),
    autoSelectValue,
  };
}

export function buildHoursList(input: BuildHoursListInput): HourListItem[] {
  const {
    scheduleTimes,
    tariffsInformation,
    hasSchedulingRequiringSeat,
    validationStatus,
    validationTariffHash,
    selectedTariffsHash,
    selectedTariffsPerType,
    validationResults,
  } = input;

  const isCurrent = isValidationCurrent(selectedTariffsHash, validationTariffHash);
  const checkSeatMap = canCheckSeatMap(hasSchedulingRequiringSeat, validationStatus, isCurrent);

  return scheduleTimes.map((hour) => {
    const occupationError = !scheduleIsAvailable(tariffsInformation, hour.minutes);
    const seatMapError =
      !occupationError &&
      checkSeatMap &&
      !hasSeatMapAvailability(hour.minutes, selectedTariffsPerType, validationResults);
    return { hour, error: occupationError || seatMapError };
  });
}

export function areAllTariffsHoursUnavailable(
  hoursList: readonly HourListItem[],
  listOfTariffsPerType: TariffsPerType[],
): boolean {
  return (
    hoursList.every((hourItem) => hourItem.error) &&
    listOfTariffsPerType.some((tariffType) => tariffType.selectedQuantity)
  );
}
